/* eslint-disable no-unused-vars */
import { TagHandlerBase } from './tagHandlerBase';
import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
const THREE = window.THREE;

/**
 * Handler for pipeline tags
 */
export class PipelineHandler extends TagHandlerBase {
    /**
     * Check if tag type should be processed
     * @param {string} type - Tag type
     * @param {Object} displaySettings - Display settings
     * @returns {boolean} Should process pipeline
     */
    shouldProcess(type, displaySettings) {
        return (displaySettings.showManualPipelines && type === 'manual_pipelines') || 
               (displaySettings.showPipelines && this.isViewer2d() && type === 'pipeline');
    }
    
    /**
     * Collect pipeline data
     * @param {Object} settings - Display settings
     * @param {Object} viewerData - Viewer position data
     * @returns {Object} Collected pipeline data
     */
    collectData(settings, viewerData) {
        const { isGlobalSearchFilter, displaySettings } = settings;
        const data = {};
        
        // Clear any existing cluster text meshes
        this.clearClusterTextMeshes();
        
        // Get pipeline type from extension
        let type = '';
        if (this.extension.currentTagType) {
            type = this.extension.currentTagType;
        } else {
            // Try to determine type based on available information
            if (displaySettings.showManualPipelines) {
                type = 'manual_pipelines';
            } else if (displaySettings.showPipelines && this.isViewer2d()) {
                type = 'pipeline';
            } else {
                return data; // No applicable type
            }
        }
        
        // Skip if not applicable
        if (!this.shouldProcess(type, displaySettings)) return data;
        
        let pipelines = this.getPipelinesByType(type);

        if (isGlobalSearchFilter) {
            let isolatedTags = store.getters['GET_ISOLATED_IDS'];    
            if (isolatedTags.size > 0) {
                pipelines = pipelines.filter(t => isolatedTags.has(t.id));
            }
        }
        
        // Filter pipelines before clustering
        pipelines = pipelines.filter(pipeline => {
            // Comment out global search filtering for now
            /*
            // Skip if filtered out by global search
            if (this.shouldSkipPipeline(pipeline, type, isGlobalSearchFilter)) return false;
            */
            
            return true;
        });
        
        // Only apply clustering after filtering in 2D view
        if (this.isViewer2d() && pipelines.length > 0) {
            // Apply clustering logic
            const { items, clusters } = this.getTagsClustersAndItems2D(pipelines, viewerData.position);

            // Process clusters
            this.processClusters(clusters, data, 'pipeline-cluster');
            
            // Update to use filtered items
            pipelines = items;
        }
        
        pipelines.forEach(pipeline => {
            // Get point in viewer space
            let point = this.getPointForPipeline(pipeline, type);
            if (!point) return;
            
            // Create point cloud data
            const pcData = this.createPipelineData(pipeline, point);
            
            // Add to category collection
            const category = pipeline.category ?? 1;
            data[category] ? data[category].push(pcData) : data[category] = [pcData];
        });
        
        return data;
    }
    
    /**
     * Get pipelines for specific type
     * @param {string} type - Pipeline type
     * @returns {Array} Pipeline items
     */
    getPipelinesByType(type) {
        if (type === 'manual_pipelines') {
            // Manual pipelines
            const tags = store.state.forgeService.getManualPipelineTagsInLevel(store.state.selectedLevel);
            return this.isViewer2d() ? tags : store.getters.GET_MANUAL_PIPELINES_TAGS;
        } else if (type === 'pipeline') {
            // Auto pipelines
            return store.state.forgeService.getPipelineTagsInLevel(store.state.selectedLevel);
        }
        return [];
    }
    
    /**
     * Check if pipeline should be skipped based on filters
     * @param {Object} pipeline - Pipeline object
     * @param {string} type - Tag type
     * @param {boolean} isGlobalSearchFilter - Global search filter flag
     * @returns {boolean} Should skip pipeline
     */
    shouldSkipPipeline(pipeline, type, isGlobalSearchFilter) {
        if (type === 'manual_pipelines') {
            return isGlobalSearchFilter && !store.state.filteredTags.includes(pipeline);
        } else if (type === 'pipeline') {
            const filteredTagsIds = store.getters.GET_FILTERED_PIPELINES_TAGS_IDS;
            return isGlobalSearchFilter && !filteredTagsIds.some(id => id == pipeline.id);
        }
        return false;
    }
    
    /**
     * Get point in viewer space for pipeline
     * @param {Object} pipeline - Pipeline object
     * @param {string} type - Tag type
     * @returns {Object|null} Point in viewer space
     */
    getPointForPipeline(pipeline, type) {
        if (!pipeline) {
            debugWarn('pipelineHandler', 'Pipeline is null or undefined');
            return null;
        }

        let point;
        
        if (type === 'pipeline') {
            pipeline.point = Object.assign(new THREE.Vector3(), pipeline.point);
            point = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(pipeline.point);
            point.x += .001; // Offset for visibility
        } else {
            point = this.isViewer2d() 
                ? store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(pipeline.point) 
                : pipeline.getPointInView();
                
            // Skip if outside crop box in 3D view
            if (!this.isViewer2d() && !store.state.forgeService.getMainExtension()?.isWithinCropBox(point)) {
                return null;
            }
            
            if (this.isViewer2d()) point.z = .1;
        }
        
        return point;
    }
    
    /**
     * Create pipeline data for point cloud
     * @param {Object} pipeline - Pipeline object
     * @param {Object} point - Point in viewer space
     * @returns {Object} Point cloud data
     */
    createPipelineData(pipeline, point) {
        const _this = this;
        return {
            name: pipeline.name,
            thumbnailURL: pipeline.thumbnailUrl,
            object: pipeline,
            point: point,
            onClick: (event) => {
                store.dispatch('FETCH_PIPELINE_TAG_BY_ID', pipeline.id).then(tag => {
                    store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                });
            }
        };
    }
}
